import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  AppLayout,
  Box,
  Button,
  Container,
  ContentLayout,
  Grid,
  Icon,
  Input,
  KeyValuePairs,
  Link,
  SpaceBetween,
} from "@amzn/awsui-components-react/polaris";

import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";

import SideNav from "../../Home/SideNav";
import ControlPanelModal from "./ControlPanelModal";
import AddColumnModal from "./AddColumnModal";
import RowSettingsModal from "./RowSettingsModal";
import RoleAliasCellEditor from "./RoleAliasCellEditor";
import CreateTaskManagerModal from "./CreateTaskManagerModal";
import AdhocTaskManagerModal from "./AdhocTaskManagerModal";
import { selectIsEffectiveAdmin, selectIsEffectiveSupportAdmin } from "../../utilities/adminUtils";
import { captureMetrics } from "src/katalMetrics/metricsHelperMethods";
import { ACTION_METHODS, METRIC_TYPE, SITE_SERVICES } from "src/katalMetrics/metricsModels";
import { TASK_MANAGER_ROUTE } from "./constants";
import { listDashboards, getServices, getLogoViewUrl, fetchAllApps } from "./api";
import { getCachedLdaps } from "src/checkPageAccess";
// Reuse the CorpReporting deliverables styling (small_values, info_message tags, etc.)
// so this page matches the look-and-feel of the CRA deliverables dashboard.
import "../CorpReporting/styles.scss";

const asList = (v) => (Array.isArray(v) ? v : []);

// The dashboard logo (or a default Cloudscape icon tile when no logo is set).
const DashboardLogo = ({ name, logoUrl }) =>
  logoUrl ? (
    <img
      src={logoUrl}
      alt={`${name} logo`}
      style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 8, flexShrink: 0 }}
    />
  ) : (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 8,
        background: "#ec7211",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon name="multiscreen" variant="inverted" size="medium" />
    </div>
  );

// A single metric value styled as a KPI number, reusing Cloudscape Box
// typography so it inherits the portal font.
const Metric = ({ value, danger }) => (
  <Box variant="h1" color={danger ? "text-status-error" : "text-status-info"} padding="n">
    {value}
  </Box>
);

// Top summary section built entirely from Cloudscape primitives: a Grid splits
// the row into the identity Container and the metrics Container, and
// KeyValuePairs renders the deliverable counts as a labelled KPI strip.
const SummaryBar = ({
  name,
  logoUrl,
  allDeliverables,
  notDue,
  dueToday,
  overdue,
  canManage,
  canEdit,
  onToggleAdmin,
  onEdit,
}) => (
  <Grid
    gridDefinition={[
      { colspan: { default: 12, s: 3 } },
      { colspan: { default: 12, s: 9 } },
    ]}
  >
    <Container>
      <SpaceBetween direction="horizontal" size="s" alignItems="center">
        <DashboardLogo name={name} logoUrl={logoUrl} />
        <SpaceBetween size="xxs">
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Box variant="h2" padding="n">
              {name}
            </Box>
            {canEdit && (
              <Button
                variant="inline-icon"
                iconName="edit"
                ariaLabel="Edit dashboard"
                onClick={onEdit}
              />
            )}
          </SpaceBetween>
          {canManage && (
            <Button variant="inline-link" iconName="settings" onClick={onToggleAdmin}>
              Admin controls
            </Button>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </Container>

    <Container>
      <KeyValuePairs
        columns={4}
        items={[
          { label: "All Deliverables", value: <Metric value={allDeliverables} /> },
          { label: "Not Due", value: <Metric value={notDue} /> },
          { label: "Due Today", value: <Metric value={dueToday} /> },
          { label: "Overdue", value: <Metric value={overdue} danger /> },
        ]}
      />
    </Container>
  </Grid>
);

// preparer = uploaders. Keep the typed shape ({ alias, type }) so the editor
// can offer USER (UPLOADER) vs LDAP (UPLOADER_LDAP).
const preparersOf = (svc) =>
  asList(svc.uploaders)
    .filter((u) => u && u.alias)
    .map((u) => ({ alias: u.alias, type: u.type || "UPLOADER" }));

// approvers list carries column_index: 1 = approver, 2 = reviewer. Keep the
// typed shape ({ alias, type, column_index }) for the editor + save.
const approverRoleOf = (svc, columnIndex) =>
  asList(svc.approvers)
    .filter((a) => a && a.alias && Number(a.column_index) === columnIndex)
    .map((a) => ({ alias: a.alias, type: a.type || "APPROVER", column_index: columnIndex }));

// Bare aliases from a typed role array (for search text / membership checks).
const aliasesOf = (rows) => asList(rows).map((r) => r && r.alias).filter(Boolean);

// Quick-filter text for a role column: the aliases joined, so the search box
// still matches even though the cell value is an array of objects.
const roleQuickFilterText = (params) => aliasesOf(params.value).join(" ");

// Display renderer for a role column: groups the typed alias rows into a USER
// line and an LDAP line (mirrors DeliverablesPage renderAliases).
const RoleCell = (params) => {
  const rows = asList(params.value);
  const users = rows
    .filter((r) => r && r.type && !String(r.type).endsWith("_LDAP"))
    .map((r) => r.alias)
    .filter(Boolean);
  const ldaps = rows
    .filter((r) => r && String(r.type).endsWith("_LDAP"))
    .map((r) => r.alias)
    .filter(Boolean);
  return (
    <Box>
      {users.length > 0 && (
        <div>
          <span className="small_values">{users.join(", ")}</span>
        </div>
      )}
      {ldaps.length > 0 && (
        <div>
          <span className="small_values">{ldaps.join(", ")}</span>
        </div>
      )}
    </Box>
  );
};

// ---- Deliverable tag helpers (mirror CorpReporting/DeliverablesPage) ----

// The DISABLED custom attribute shows the grey "Disabled" badge.
const isDisabledOf = (svc) =>
  (asList(svc.custom_attributes).find((a) => a && a.attribute_type === "DISABLED")?.attribute || "false") === "true";

// Treat null/undefined/blank ids as "missing" so we never build a broken link
// like /App/1/flexico//upload. Returns the trimmed id string, or null.
const idOrNull = (val) => {
  if (val === null || val === undefined) return null;
  const str = String(val).trim();
  return str === "" ? null : str;
};

// The FRISCO process id for this service (null when absent, e.g. LOGICO rows).
const processIdOf = (svc) => idOrNull(svc?.process_id ?? svc?.processId ?? svc?.pid);

// The Flexico flex/template id for this service (null when absent).
const flexIdOf = (svc) => idOrNull(svc?.flex_id ?? svc?.flexId ?? svc?.fid);

// The Logico process id for this service (null when absent).
const logicoProcessIdOf = (svc) =>
  idOrNull(svc?.logico_process_id ?? svc?.logicoProcessId ?? svc?.lpid);

/**
 * Build the destination for a deliverable, or null if we can't route it.
 * Mirrors CorpReporting/DeliverablesPage: a service is Flexico when it carries
 * a `flex_id` (-> flexico execution/upload page), Logico when it carries a
 * `logico_process_id` (-> logico upload page), otherwise FRISCO via its
 * `process_id` (-> process upload page). All need the owning `app_id`.
 *
 * Any service missing the required ids (a row with no id) simply returns null
 * and renders as plain, non-clickable text — never a crash.
 */
const deliverableHrefOf = (svc) => {
  const appId = idOrNull(svc?.app_id ?? svc?.appId);
  if (!appId) return null;
  const flexId = flexIdOf(svc);
  if (flexId) {
    return `/App/${appId}/flexico/${flexId}/upload`;
  }
  const logicoProcessId = logicoProcessIdOf(svc);
  if (logicoProcessId) {
    return `/App/${appId}/LogicoProcess/${logicoProcessId}/upload`;
  }
  const processId = processIdOf(svc);
  if (processId) {
    return `/App/${appId}/process/${processId}/upload`;
  }
  return null;
};

/**
 * Task Manager dashboard detail page.
 *
 * Unique per-dashboard URL: /taskmanager/:dashboardId (e.g. /taskmanager/3).
 * Renders the services (FRISCO / FLEXICO / LOGICO) associated with the
 * dashboard's apps in an ag-grid table. getServices returns each service with
 * raw uploaders + approvers lists; the UI derives Preparer (uploaders),
 * Approver (approvers column_index 1) and Reviewer (approvers column_index 2).
 *
 * The API returns at most 100 rows per call plus `hasMore` / `nextOffset`;
 * "Load more" pulls the next page and appends.
 */
const TaskManagerDashboard = () => {
  const { dashboardId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const gridRef = useRef(null);
  // Guards against React 18 StrictMode's double effect invoke (dev), which
  // would otherwise fire getServices twice for the same dashboard.
  const fetchedDashboardId = useRef(null);

  // Seed from the dashboard passed via router state (from the list page) so the
  // title shows the name right away, even before the data load resolves.
  const [dashboard, setDashboard] = useState(location.state?.dashboard || null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [nextOffset, setNextOffset] = useState(null);
  // Server-reported total across all pages, so the "All Deliverables" KPI is
  // accurate before every page is loaded (rowData only holds fetched pages).
  const [totalCount, setTotalCount] = useState(null);
  const [navOpen, setNavOpen] = useState(true);
  const [quickFilter, setQuickFilter] = useState("");

  // Gear (settings) + "Add column" are admin-only actions on the dashboard header.
  const isPortalAdmin = useSelector(selectIsEffectiveAdmin);
  const isSupportAdmin = useSelector(selectIsEffectiveSupportAdmin);
  // Editing the dashboard itself (name/apps/logo) is restricted to portal and
  // support admins only.
  const canEditDashboard = isPortalAdmin || isSupportAdmin;
  // Admin controls (bulk actions, gear, editable role columns) are available to
  // portal/support admins plus an additional set of users (to be wired later).
  const canManage = isPortalAdmin || isSupportAdmin;
  // Current user + their LDAP groups, used to scope the grid for non-admins to
  // only the deliverables they preparer / review / approve.
  const userId = useSelector((s) => s.auth.userId);
  const userLDAPS = useSelector((s) => s.auth.userLDAPS);
  const currentStage = useSelector((s) => s.auth.currentStage);
  // Resolved LDAP group memberships (same source DeliverablesPage uses for
  // "my deliverables"), so LDAP-typed roles the user belongs to also match.
  const [ldapGroups, setLdapGroups] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  // Edit-dashboard dialog (name / apps / logo), reusing the listing-page modal.
  const [editOpen, setEditOpen] = useState(false);
  // "Adhoc task manager" dialog — collects an arbitrary list of strings.
  const [adhocOpen, setAdhocOpen] = useState(false);
  // Per-row "Row Settings" dialog opened from the admin gear.
  const [rowSettingsRow, setRowSettingsRow] = useState(null);
  // Rows currently checked in the grid; drives the bulk admin actions.
  const [selectedRows, setSelectedRows] = useState([]);
  // Toggles the small admin-controls panel shown to the right of the grid.
  const [adminControlsOpen, setAdminControlsOpen] = useState(false);
  // "Save view" is enabled only when the current columns differ from the
  // saved/default baseline.
  const [viewDirty, setViewDirty] = useState(false);
  const baselineColStateRef = useRef(null);
  // app_id -> app name, for the "App" grid column.
  const [appsById, setAppsById] = useState({});

  const loadPage = async (offset) => {
    const result = await getServices({ dashboard_id: Number(dashboardId), offset });
    const rows = asList(result?.services);
    setServices((prev) => (offset ? [...prev, ...rows] : rows));
    setNextOffset(result?.hasMore ? result.nextOffset : null);
    // Prefer the server total (constant across pages) so the KPI reflects the
    // full deliverable count, not just the pages fetched so far.
    if (typeof result?.total === "number") setTotalCount(result.total);
  };

  /**
   * Load everything the detail page needs from the first page onwards.
   * Centralizes all API integrations for this page so future ones (e.g. status,
   * trackers) can be added here and picked up by both the initial load and the
   * Refresh button. `cancelledRef` (optional) lets the mount effect abort stale
   * state updates on unmount.
   */
  const loadData = async () => {
    setLoading(true);
    setLoadError("");

    // Dashboard metadata (name/logo). Kept independent of the services fetch so
    // a services failure never blocks the title, and vice versa. GET_DASHBOARD
    // isn't implemented yet, so we resolve the name from the list. Timed
    // separately from services so we can see each call's latency.
    const dashboardStartedAt = Date.now();
    try {
      const dashboards = await listDashboards();
      const match = asList(dashboards).find(
        (d) => String(d.dashboard_id) === String(dashboardId)
      );
      if (match) setDashboard(match);
      captureMetrics(
        ACTION_METHODS.PAGE_LOAD_TIME,
        [
          {
            metricType: METRIC_TYPE.TIMER,
            metricName: "TaskManagerDashboardInfoLoadTime",
            metricValue: Date.now() - dashboardStartedAt,
            isMonitor: true,
          },
        ],
        SITE_SERVICES.TASK_MANAGER
      );
    } catch (e) {
      // Non-fatal for the title; keep whatever we already have (router state).
    }

    // Services list. Publish a Katal timer metric for how long the services
    // load takes, so we can monitor getServices latency from the UI.
    const startedAt = Date.now();
    try {
      await loadPage(0);
      captureMetrics(
        ACTION_METHODS.PAGE_LOAD_TIME,
        [
          {
            metricType: METRIC_TYPE.TIMER,
            metricName: "TaskManagerServicesLoadTime",
            metricValue: Date.now() - startedAt,
            isMonitor: true,
          },
        ],
        SITE_SERVICES.TASK_MANAGER
      );
    } catch (e) {
      setLoadError(e?.errors?.[0]?.message || e?.message || "Failed to load services.");
      setServices([]);
      setNextOffset(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Dedupe React 18 StrictMode's double effect invoke for the same id. The
    // ref alone is enough — do NOT also cancel on cleanup, or the (only) first
    // run's state writes get thrown away and nothing ever loads.
    if (fetchedDashboardId.current === dashboardId) return;
    fetchedDashboardId.current = dashboardId;
    loadData();

    // App metadata (shared local cache) so the grid can show the app name per
    // deliverable, keyed by app_id.
    fetchAllApps()
      .then((apps) => {
        const map = {};
        (apps || []).forEach((a) => {
          map[String(a.app_id)] = a.app_name;
        });
        setAppsById(map);
      })
      .catch(() => setAppsById({}));
  }, [dashboardId]);

  // Resolve the current user's LDAP group memberships once, so non-admins also
  // see deliverables whose role is an LDAP group they belong to.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(getCachedLdaps(currentStage))
      .then((raw) => {
        if (cancelled) return;
        const list = typeof raw === "string" ? JSON.parse(raw || "[]") : raw || [];
        setLdapGroups(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setLdapGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentStage]);

  // Resolve the stored logo object key to a short-lived viewable URL via Vault,
  // re-running whenever the dashboard's logo changes.
  useEffect(() => {
    let cancelled = false;
    const key = dashboard?.logo;
    if (!key) {
      setLogoUrl(null);
      return undefined;
    }
    getLogoViewUrl(key)
      .then((url) => {
        if (!cancelled) setLogoUrl(url);
      })
      .catch(() => {
        if (!cancelled) setLogoUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dashboard?.logo]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData();
    } finally {
      setRefreshing(false);
    }
  };

  const handleLoadMore = async () => {
    if (nextOffset == null) return;
    setLoadingMore(true);
    setLoadError("");
    try {
      await loadPage(nextOffset);
    } catch (e) {
      setLoadError(e?.errors?.[0]?.message || e?.message || "Failed to load more services.");
    } finally {
      setLoadingMore(false);
    }
  };

  // ---- Saved grid view (column visibility / order / width) ----
  // Persisted per dashboard. Backed by localStorage for now; swap for a backend
  // mutation once the view-persistence API is available.
  const COLUMN_STATE_KEY = `taskManager.columnState.${dashboardId}`;

  // ag-grid moved getColumnState/applyColumnState from columnApi to gridApi in
  // newer versions; support both.
  const getGridColApi = () => gridRef.current?.columnApi || gridRef.current?.api || null;

  // ---- Row selection + bulk admin actions ----
  const onSelectionChanged = () => {
    const api = gridRef.current?.api;
    const rows = api ? api.getSelectedRows() : [];
    setSelectedRows(rows);
    // Expand the admin controls so the bulk actions are visible on selection.
    if (rows.length > 0 && canManage) setAdminControlsOpen(true);
  };

  const hasSelection = selectedRows.length > 0;

  // Hide / Unhide / Enable / Disable the checked deliverables. Backend wiring is
  // pending; for now this just clears the selection after acting.
  const handleBulkAction = (action) => {
    if (!hasSelection) return;
    // TODO: call the task-manager mutation for `action` with selectedRows once
    // the endpoint is available.
    gridRef.current?.api?.deselectAll?.();
    setSelectedRows([]);
  };

  // Commit hook for the editable role columns (Preparer / Reviewer / Approver).
  // The value setter has already written the new alias list onto the grid row;
  // this is where the change must be persisted. The save mutation isn't wired
  // yet, so surface a non-blocking notice rather than letting the edit look
  // saved while it is only in-memory (and lost on the next rowData recompute).
  const handleRoleCellEdited = (event) => {
    if (!canManage) return;
    // TODO: persist event.data[event.colDef.field] for
    // event.data.deliverable via the task-manager update mutation once the
    // endpoint is available, then reload on success.
    setLoadError(
      "Role changes aren't saved yet — editing deliverable roles is coming soon."
    );
  };

  // Normalize column state to just the parts a "view" cares about (visibility +
  // order) so resize noise doesn't mark the view dirty.
  const normalizeColState = (state) =>
    JSON.stringify((state || []).map((c) => ({ colId: c.colId, hide: !!c.hide })));

  const currentColStateString = () => {
    const api = getGridColApi();
    return normalizeColState(api?.getColumnState?.());
  };

  // Recompute whether the current columns differ from the saved/default
  // baseline. When they do, enable "Save view" and auto-open the admin panel.
  const recomputeViewDirty = () => {
    const dirty = currentColStateString() !== baselineColStateRef.current;
    setViewDirty(dirty);
    if (dirty) setAdminControlsOpen(true);
  };

  const applySavedColumnState = () => {
    const api = getGridColApi();
    try {
      const saved = localStorage.getItem(COLUMN_STATE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        api?.applyColumnState?.({ state, applyOrder: true });
      }
    } catch (e) {
      /* ignore malformed saved state */
    }
    // Whatever is applied now (saved or default) becomes the clean baseline.
    baselineColStateRef.current = currentColStateString();
    setViewDirty(false);
  };

  const handleSaveView = () => {
    const api = getGridColApi();
    const state = api?.getColumnState?.();
    if (!state) return;
    try {
      localStorage.setItem(COLUMN_STATE_KEY, JSON.stringify(state));
    } catch (e) {
      /* storage unavailable — ignore */
    }
    baselineColStateRef.current = normalizeColState(state);
    setViewDirty(false);
  };

  const handleResetView = () => {
    const api = getGridColApi();
    api?.resetColumnState?.();
    try {
      localStorage.removeItem(COLUMN_STATE_KEY);
    } catch (e) {
      /* ignore */
    }
    baselineColStateRef.current = currentColStateString();
    setViewDirty(false);
  };

  const rowData = useMemo(
    () => {
      // Identities that count as "mine": the logged-in user plus any LDAP groups
      // they belong to (aliases are stored as bare logins / group names).
      const myIdentities = new Set(
        [userId, ...asList(userLDAPS), ...asList(ldapGroups)]
          .filter(Boolean)
          .map((s) => String(s).toLowerCase())
      );
      const isMine = (row) =>
        [
          ...aliasesOf(row.preparer),
          ...aliasesOf(row.approver),
          ...aliasesOf(row.reviewer),
        ].some((a) => myIdentities.has(String(a).toLowerCase()));

      const rows = services.map((svc) => {
        const appId = svc.app_id ?? svc.appId ?? null;
        return {
          deliverable: svc.deliverable || "",
          deliverableHref: deliverableHrefOf(svc),
          disabled: isDisabledOf(svc),
          source: svc.source || "",
          preparer: preparersOf(svc),
          approver: approverRoleOf(svc, 1),
          reviewer: approverRoleOf(svc, 2),
          appId: idOrNull(appId),
          appName: appId != null ? appsById[String(appId)] || String(appId) : "",
          serviceType: svc.source || "",
        };
      });

      // Admins see everything; non-admins only the deliverables they preparer /
      // review / approve.
      return canManage ? rows : rows.filter(isMine);
    },
    [services, appsById, canManage, userId, userLDAPS, ldapGroups]
  );

  // Render the deliverable as an in-app link when we can route it (FRISCO ->
  // process upload page, Flexico -> flexico execution page); otherwise plain
  // text. Uses history.push (with a real href for open-in-new-tab support)
  // instead of a full page reload.
  // Render the deliverable like the CRA deliverables dashboard: the name as a
  // small link (FRISCO -> process upload page, Flexico -> flexico execution
  // page), with a service tag underneath ("Automated" for Flexico).
  const DeliverableCell = (params) => {
    const value = params.value || "";
    const data = params.data || {};
    const href = data.deliverableHref;
    // Always render the deliverable name as a link. When we have a routable href
    // (FRISCO process / Flexico upload page), clicking navigates in-app.
    // Otherwise (backend not yet returning ids) it renders as a link but is a
    // no-op on click — placeholder until the ids ship.
    const nameNode = (
      <Link
        href={href || "#"}
        variant="primary"
        onFollow={(e) => {
          if (!href) {
            if (e?.preventDefault) e.preventDefault();
            return;
          }
          // Cloudscape Link fires onFollow for a normal click; take over routing
          // in-app. Modified clicks (new tab/window) keep the native href.
          if (e?.detail?.ctrlKey || e?.detail?.metaKey || e?.detail?.shiftKey || e?.detail?.button === 1) {
            return;
          }
          if (e?.preventDefault) e.preventDefault();
          history.push(href);
        }}
      >
        <span className="small_values">{value}</span>
      </Link>
    );

    return (
      <Box>
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {/* Admin-only gear to the left of the deliverable name -> opens the
              per-row settings dialog. */}
          {canManage && (
            <Button
              variant="inline-icon"
              iconName="settings"
              ariaLabel="Row settings"
              onClick={() => setRowSettingsRow(data)}
            />
          )}
          <SpaceBetween direction="vertical" size="xxs">
            {nameNode}

            {/* Disabled badge for disabled deliverables. */}
            {data.disabled && (
              <span className="smaller_values grey_badge">Disabled</span>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Box>
    );
  };

  // Render the app name as an in-app link to the app details page
  // (/App/:id). Falls back to plain text when the row has no app id.
  const AppCell = (params) => {
    const value = params.value || "";
    const appId = params.data?.appId;
    if (!appId) {
      return <span className="small_values">{value}</span>;
    }
    const href = `/App/${appId}`;
    return (
      <Link
        href={href}
        variant="primary"
        onFollow={(e) => {
          // Modified clicks (new tab/window) keep the native href.
          if (e?.detail?.ctrlKey || e?.detail?.metaKey || e?.detail?.shiftKey || e?.detail?.button === 1) {
            return;
          }
          if (e?.preventDefault) e.preventDefault();
          history.push(href);
        }}
      >
        <span className="small_values">{value}</span>
      </Link>
    );
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        colId: "__select",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        pinned: "left",
        resizable: false,
        sortable: false,
        filter: false,
        suppressMenu: true,
        lockPosition: true,
        suppressSizeToFit: true,
      },
      // Deliverable / App / Service Type stay read-only. The alias role columns
      // (Preparer / Reviewer / Approver) are editable for admins — type a
      // comma-separated list of aliases.
      { headerName: "Deliverable", field: "deliverable", cellRenderer: DeliverableCell, autoHeight: true, flex: 2, minWidth: 260 },
      {
        headerName: "Preparer",
        field: "preparer",
        cellRenderer: RoleCell,
        editable: canManage,
        cellEditor: RoleAliasCellEditor,
        cellEditorPopup: true,
        cellEditorParams: { role: "preparer" },
        valueSetter: (p) => {
          p.data.preparer = asList(p.newValue);
          return true;
        },
        // Filter/search on the alias strings, not the raw {alias,type} objects.
        filterValueGetter: (p) => aliasesOf(p.data?.preparer),
        getQuickFilterText: roleQuickFilterText,
        autoHeight: true,
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: "Reviewer",
        field: "reviewer",
        cellRenderer: RoleCell,
        editable: canManage,
        cellEditor: RoleAliasCellEditor,
        cellEditorPopup: true,
        cellEditorParams: { role: "reviewer", columnIndex: 2 },
        valueSetter: (p) => {
          p.data.reviewer = asList(p.newValue);
          return true;
        },
        filterValueGetter: (p) => aliasesOf(p.data?.reviewer),
        getQuickFilterText: roleQuickFilterText,
        autoHeight: true,
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: "Approver",
        field: "approver",
        cellRenderer: RoleCell,
        editable: canManage,
        cellEditor: RoleAliasCellEditor,
        cellEditorPopup: true,
        cellEditorParams: { role: "approver", columnIndex: 1 },
        valueSetter: (p) => {
          p.data.approver = asList(p.newValue);
          return true;
        },
        filterValueGetter: (p) => aliasesOf(p.data?.approver),
        getQuickFilterText: roleQuickFilterText,
        autoHeight: true,
        flex: 1,
        minWidth: 160,
      },
      { headerName: "App", field: "appName", cellRenderer: AppCell, flex: 1, minWidth: 140 },
      { headerName: "Service Type", field: "serviceType", flex: 1, minWidth: 130 },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage]
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: true }),
    []
  );

  const content = (
    <ContentLayout defaultPadding={true}>
      <SpaceBetween size="m">
        <SummaryBar
          name={dashboard?.name || `Dashboard ${dashboardId}`}
          logoUrl={logoUrl}
          // Admins see every deliverable, so use the server total (accurate
          // before all pages load). Non-admins see only their scoped rows, for
          // which the loaded row count is the right figure.
          allDeliverables={canManage ? (totalCount ?? rowData.length) : rowData.length}
          notDue={0}
          dueToday={0}
          overdue={0}
          canManage={canManage}
          canEdit={canEditDashboard}
          onToggleAdmin={() => setAdminControlsOpen((o) => !o)}
          onEdit={() => setEditOpen(true)}
        />

        {canManage && adminControlsOpen && (
          <Container>
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Box variant="h4" padding="n">
                Admin controls
              </Box>
              <Button iconName="settings" onClick={() => setSettingsOpen(true)}>
                Settings
              </Button>
              <Button iconName="add-plus" onClick={() => setAddColumnOpen(true)}>
                Add column
              </Button>
              <Button iconName="check" onClick={handleSaveView} disabled={!viewDirty}>
                Save view
              </Button>
              <Button iconName="undo" onClick={handleResetView}>
                Reset view
              </Button>
              <Button disabled={!hasSelection} onClick={() => handleBulkAction("HIDE")}>
                Hide
              </Button>
              <Button disabled={!hasSelection} onClick={() => handleBulkAction("UNHIDE")}>
                Unhide
              </Button>
              <Button disabled={!hasSelection} onClick={() => handleBulkAction("ENABLE")}>
                Enable
              </Button>
              <Button disabled={!hasSelection} onClick={() => handleBulkAction("DISABLE")}>
                Disable
              </Button>
              <Button variant="primary" onClick={() => setAdhocOpen(true)}>
                Adhoc task manager
              </Button>
            </SpaceBetween>
          </Container>
        )}

        {loadError && (
          <Alert type="error" header="Couldn't load services" dismissible onDismiss={() => setLoadError("")}>
            {loadError}
          </Alert>
        )}

        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
          <div style={{ flex: 1, maxWidth: 800 }}>
            <Input
              type="search"
              value={quickFilter}
              placeholder="Search services..."
              onChange={({ detail }) => setQuickFilter(detail.value)}
            />
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <Button iconName="refresh" onClick={handleRefresh} loading={refreshing}>
              Refresh
            </Button>
            <Button iconName="arrow-left" onClick={() => history.push(TASK_MANAGER_ROUTE)}>
              Back
            </Button>
          </div>
        </div>

        <div className="ag-theme-alpine" style={{ width: "100%", height: "70vh" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            quickFilterText={quickFilter}
            animateRows={true}
            rowHeight={32}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            enableCellTextSelection={true}
            onGridReady={applySavedColumnState}
            onSelectionChanged={onSelectionChanged}
            onDisplayedColumnsChanged={recomputeViewDirty}
            onCellValueChanged={handleRoleCellEdited}
            overlayNoRowsTemplate={
              loading
                ? "Loading services..."
                : "No services are associated with this dashboard's apps yet."
            }
          />
        </div>

        {nextOffset != null && (
          <Box textAlign="center">
            <Button onClick={handleLoadMore} loading={loadingMore}>
              Load more
            </Button>
          </Box>
        )}
      </SpaceBetween>

      <ControlPanelModal
        visible={settingsOpen}
        dashboard={dashboard}
        onDismiss={() => setSettingsOpen(false)}
        onSave={() => setSettingsOpen(false)}
      />

      <RowSettingsModal
        visible={rowSettingsRow != null}
        row={rowSettingsRow}
        onDismiss={() => setRowSettingsRow(null)}
        onSave={() => setRowSettingsRow(null)}
      />

      <AdhocTaskManagerModal
        visible={adhocOpen}
        onDismiss={() => setAdhocOpen(false)}
        onSave={() => setAdhocOpen(false)}
      />

      <CreateTaskManagerModal
        visible={editOpen}
        dashboard={dashboard}
        onDismiss={() => setEditOpen(false)}
        onSaved={(updated) => {
          // Merge the saved fields so the title/logo update immediately, then
          // refresh the services (the app associations may have changed).
          setDashboard((prev) => ({ ...(prev || {}), ...(updated || {}) }));
          setEditOpen(false);
          loadData();
        }}
      />

      <AddColumnModal
        visible={addColumnOpen}
        onDismiss={() => setAddColumnOpen(false)}
        onAdd={() => setAddColumnOpen(false)}
      />
    </ContentLayout>
  );

  return (
    <AppLayout
      disableContentPaddings={true}
      content={content}
      contentType="default"
      toolsHide={true}
      navigationOpen={navOpen}
      onNavigationChange={() => setNavOpen(!navOpen)}
      navigation={<SideNav activeHref={TASK_MANAGER_ROUTE} />}
    />
  );
};

export default TaskManagerDashboard;
